import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { DSButton, DSBadge, DSIcon, DSAlert } from '../design-system/primitives';
import PinGolfBottomNav from '../components/PinGolfBottomNav';
import pingolfHero from '../assets/pingolf-hero.webp';
import './pingolfHeroShared.css';
import './PinGolfLeagueAdmin.css';

const SEASON_STATUS_VARIANT = {
  active: 'success',
  completed: 'warning'
};

function PinGolfLeagueAdmin() {
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [loadError, setLoadError] = useState('');

  // A failed load used to reject unhandled, so the whole app was replaced by the dev error
  // overlay instead of this screen reporting the problem. The empty state cannot be allowed
  // to win either: "No seasons yet" reads as a season list that loaded and came back empty.
  const fetchSeasons = useCallback(async () => {
    try {
      setLoadError('');
      const result = await apiService.getPingolfSeasons();
      setSeasons(result.data);
    } catch (err) {
      console.error('Failed to load PinGolf seasons:', err);
      setSeasons([]);
      setLoadError('Could not load seasons. The league API did not respond.');
    }
  }, []);

  useEffect(() => { fetchSeasons(); }, [fetchSeasons]);

  return (
    <div className="pl-admin-page bn-page-padding">
      <div className="pingolf-hero pl-admin-list-hero">
        <img src={pingolfHero} alt="" className="pingolf-hero-banner" />
        <h1>PinGolf League</h1>
      </div>
      <p className="pl-admin-page-description">Create and run PinGolf League seasons.</p>

      <div className="pl-admin-season-list-actions">
        <DSButton
          onClick={() => navigate('/pingolf-season/admin/new')}
          leadingIcon={<DSIcon name="plus" size="sm" tone="default" />}
        >
          Create season
        </DSButton>
      </div>

      <div className="pl-admin-season-list">
        {seasons.map(s => (
          <div
            key={s.id}
            className="pl-admin-season-list-item"
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/pingolf-season/admin/${s.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/pingolf-season/admin/${s.id}`);
              }
            }}
          >
            <div className="pl-admin-season-list-item-info">
              <span className="pl-admin-season-list-item-name">{s.name}</span>
              <span className="pl-admin-season-list-item-meta">Season {s.season_number}</span>
            </div>
            <DSBadge variant={SEASON_STATUS_VARIANT[s.status] || 'info'} appearance="outline">{s.status}</DSBadge>
            <DSIcon name="chevronRight" size="sm" tone="muted" />
          </div>
        ))}
        {loadError ? (
          <div className="pl-admin-empty">
            <DSAlert variant="error">{loadError}</DSAlert>
            <DSButton tone="secondary" appearance="outline" onClick={fetchSeasons}>Retry</DSButton>
          </div>
        ) : (
          seasons.length === 0 && <div className="pl-admin-empty">No seasons yet. Create one to get started.</div>
        )}
      </div>

      <PinGolfBottomNav />
    </div>
  );
}

export default PinGolfLeagueAdmin;
